'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { mapProduct } from 'helpers/algolia/map-product';
import { canonicalAttributePath } from 'helpers/algolia/attribute-paths';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import { Tiempos } from 'fonts';
import ScrollToTopButton from 'components/commercetools-ui/atoms/scroll-to-top';
import ProductTile from '../product-tile';
import ViewToggle from '../product-list/components/view-toggle';
import CurrentRefinements from './components/current-refinements';
import CollectionFacets from './components/collection-facets';
import DesktopFacets from './components/desktop-facets';
import MobileFacets from './components/mobile-facets';
import DesktopSortFacets from './components/desktop-facets/sort-facets';
import StaticProductTile from './components/static-product-tile';
import SsrPagination from './components/pagination';
import SsrLoadMore from './components/load-more';
import SsrInfiniteScroll from './components/infinite-scroll';
import { useTileLoading, type TileLoadingStyle } from './hooks/useTileLoading';
import { useCurrentRefinements, useDatasetStatus, useHits, useInstantSearch } from './local-store';
import { useProductList } from './context';
import { FacetConfiguration, PriceConfiguration } from './types';

interface Props {
  /** Last URL segment: keys the persisted filters (localStorage). It no longer decides what is fetched. */
  slug?: string;
  /** The listing's H1 — the CMS `listing_title`. */
  title: string;
  facetsConfiguration: Record<string, FacetConfiguration>;
  pricesConfiguration: Record<string, PriceConfiguration>;
  /** CMS `listing_type` "Collection": collection chrome (centred facet pills, no sidebar). */
  isCollection?: boolean;
  /** CMS product_tiles_loading_style: 'infinite' | 'loadMore' | 'pagination' | 'all'. */
  loadingStyle?: TileLoadingStyle;
}

/**
 * Server-side-rendered Product Listing Page (SERVER PATH — every V2 listing block, never /search).
 *
 * The dataset is preloaded on the server (Redis-cached) and provided via <LocalDatasetProvider> in
 * cms-components-v2/section-with-product-listing, but only the FIRST TILE PAGE of it reaches the SSR
 * HTML as full tiles — exactly the tiles the grid shows on arrival, in the default "new first" order.
 * The provider pulls the remaining products in a single XHR right after hydration; filtering, sorting
 * and facet counts are then recomputed in the browser from that hydrated dataset (no further Algolia
 * calls). Only the visible batch mounts the interactive <ProductTile>; loaded-but-off-screen tiles
 * render a hook-free <StaticProductTile> and swap in as they scroll into range.
 *
 * Until the top-up lands the grid holds a PREFIX of the listing, so this component must not act on
 * the partial set: persisted refinements are not restored and the reveal controls are inert until
 * `isComplete`. Counts and pagination read `totalHits`, which is correct from first paint.
 */
const ProductListSSR: React.FC<Props> = ({
  slug,
  title,
  facetsConfiguration,
  pricesConfiguration,
  isCollection,
  loadingStyle,
}) => {
  const [isTabletSize] = useMediaQuery(desktop);
  const { locale } = useParams();

  const { hits } = useHits();
  const { items: currentRefinements } = useCurrentRefinements();
  const { totalHits, isComplete, queryToken } = useDatasetStatus();
  const { setUiState } = useInstantSearch();
  const { updateFacetsConfiguration, updatePricesConfiguration } = useProductList();

  const [currentView, setCurrentView] = useState<'grid' | 'list'>('grid');
  const handleViewChange = (view: 'grid' | 'list') => setCurrentView(view);

  // Push the resolved facet/price configuration into the SSR product-list context so the facet
  // components (DesktopFacets / RangeFacet / …) can read it.
  useEffect(() => {
    updateFacetsConfiguration(facetsConfiguration);
    updatePricesConfiguration(pricesConfiguration);
  }, [facetsConfiguration, pricesConfiguration, updateFacetsConfiguration, updatePricesConfiguration]);

  // ---- Filter persistence (localStorage) ----
  const storageKey = useMemo(() => `product_list_facet_filters_${locale}_${slug || 'all'}`, [locale, slug]);
  const hasHydratedRefinements = useRef(false);
  const [persistedFacetFilters, setPersistedFacetFilters] = useState<string[][]>([]);

  // Load saved filters on mount.
  useEffect(() => {
    try {
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
      if (saved) setPersistedFacetFilters(JSON.parse(saved));
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  // Persist current filters whenever they change.
  useEffect(() => {
    try {
      const groupedByAttribute = currentRefinements
        .map((facetItem) => (facetItem?.refinements || []).map((ref) => `${facetItem.attribute}:${ref.value}`))
        .filter((arr) => Array.isArray(arr) && arr.length > 0);
      if (groupedByAttribute.length > 0) window.localStorage.setItem(storageKey, JSON.stringify(groupedByAttribute));
      else window.localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  }, [currentRefinements, storageKey]);

  // Restore saved filters into the store once (after the load effect populated persistedFacetFilters).
  // Held back until the dataset is complete — restoring against the SSR prefix would filter 12 hits
  // and typically show "No products found" for as long as the top-up takes, then flip to the real
  // result. Waiting costs nothing visible: unrestored, the grid shows the correct unfiltered page 1.
  useEffect(() => {
    if (!isComplete) return;
    if (hasHydratedRefinements.current) return;
    const saved = persistedFacetFilters;
    if (!Array.isArray(saved) || saved.length === 0) return;
    const refinementList: Record<string, string[]> = {};
    for (const group of saved) {
      for (const token of group) {
        const [rawAttribute, ...rest] = String(token).split(':');
        const value = rest.join(':');
        if (!rawAttribute || value === undefined) continue;
        // Entries persisted before facet paths were canonicalised carry the Algolia-facing
        // `searchableAttributes.*` spelling. Rewriting on restore keeps them working: an uncanonical
        // path resolves to no values, and a refinement that matches nothing empties the whole grid.
        const attribute = canonicalAttributePath(rawAttribute);
        if (!refinementList[attribute]) refinementList[attribute] = [];
        if (!refinementList[attribute].includes(value)) refinementList[attribute].push(value);
      }
    }
    if (Object.keys(refinementList).length > 0) {
      setUiState((prev: any) => Object.fromEntries(Object.keys(prev ?? {}).map((k) => [k, { refinementList }])));
      hasHydratedRefinements.current = true;
    }
  }, [isComplete, persistedFacetFilters, setUiState]);

  useEffect(() => {
    hasHydratedRefinements.current = false;
  }, [storageKey]);

  const hasResults = hits.length > 0;
  const bg = isCollection ? 'bg-[#F8F8F8]' : 'bg-[#EDEDED]';

  // Tile loading style: the first tile page renders server-side, the rest arrive with the dataset
  // top-up. Loaded-but-out-of-view tiles are `hidden` (kept in the DOM; lazy images load only when
  // shown).
  //
  // The page maths run on `listingTotal`, not on how many hits happen to be loaded: while the top-up
  // is in flight `hits` is a 12-item prefix, and sizing the control to that would render "page 1 of 1"
  // and then visibly grow a moment later. `totalHits` is known from first paint, so the pagination
  // control is complete and stable immediately — it just stays inert (below) until the hits are real.
  const gridRef = useRef<HTMLDivElement | null>(null);
  const listingTotal = isComplete ? hits.length : Math.max(hits.length, totalHits);
  const { totalPages, currentPage, setPage, visibleCount, loadMore, isHidden } = useTileLoading(
    listingTotal,
    loadingStyle ?? 'infinite',
    queryToken,
  );

  const goToPage = (next: number) => {
    // Never page into hits that have not arrived yet — the control is disabled while incomplete, so
    // this is the belt-and-braces half of the same guard.
    if (!isComplete) return;
    if (next === currentPage || next < 1 || next > totalPages) return;
    setPage(next);
    // Wait for the tile swap (previous page → `hidden`, new page revealed) to fully commit before
    // scrolling — doing it synchronously lets that layout change disrupt the in-flight smooth
    // scroll, so it settled at the very top behind the fixed header instead of at the grid.
    setTimeout(() => {
      const grid = gridRef.current;
      if (!grid) return;
      // #header-container is `fixed top-0` and its height varies (maintenance bar, breakpoint), so
      // measure it and land the top of the grid just BELOW it rather than hidden behind it.
      const headerHeight = document.querySelector('#header-container')?.clientHeight ?? 0;
      const top = grid.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }, 100);
  };

  /** Reveal the next batch — bounded by LOADED hits, not by `listingTotal`. */
  const revealMore = () => {
    if (!isComplete || visibleCount >= hits.length) return;
    loadMore();
  };

  const Tiles = () => (
    <>
      {hits.map((hit: any, i: number) => {
        const product = mapProduct(hit, locale as string);
        const hidden = isHidden(i);
        return (
          // Off-screen (`hidden`) tiles render a lightweight, hook-free static tile (crawlable, ~no
          // hydration); only the visible batch mounts the interactive ProductTile. `contents` keeps
          // the tile a direct grid child so the grid layout is unaffected.
          <div key={hit.objectID ?? hit.slug ?? i} className={hidden ? 'hidden' : 'contents'}>
            {hidden ? (
              <StaticProductTile product={product} locale={locale as string} />
            ) : (
              <ProductTile
                product={product}
                layout={isCollection ? 'grid' : currentView}
                isCategoryPage={!isCollection}
                isCollectionPage={isCollection}
              />
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <>
      <div id="plp-container" className={bg}>
        <div id="container-scroll" className="flex flex-col items-end">
          {/*
            Collection listings use a different chrome from category listings: a centred row of facet
            pills under the heading, and no sidebar, sort control, view toggle or refinement chips.
            That is how the collection page looked before it moved onto this shared SSR grid, so it is
            reproduced here rather than inheriting the category layout.
          */}
          {isCollection ? (
            <>
              <h1 className="w-full px-24 pt-48 text-center text-38 font-medium leading-[3rem] text-[#090909] lg:pb-8 2xl:pb-42 2xl:pt-80 2xl:text-48">
                {title}
              </h1>
              <div className="mt-8 flex w-full items-center justify-start gap-48 px-24 py-32 md:justify-center 2xl:px-80 2xl:py-24">
                <CollectionFacets facetsConfiguration={facetsConfiguration} />
              </div>
            </>
          ) : (
            <>
              <h1
                className={`w-full pb-32 pt-48 text-center text-32 font-medium leading-8 text-[#090909] md:text-40 2xl:pb-48 2xl:pt-80 2xl:text-48 ${Tiempos.className}`}
              >
                {title}
              </h1>
              <div className="px:24 mt-8 flex w-full items-center justify-between gap-48 lg:px-48 xl:px-60 2xl:px-80">
                <div className="lg:ml-[16.5rem] 2xl:ml-[23rem]">
                  <CurrentRefinements />
                </div>
                <div className="hidden items-center gap-4 lg:flex">
                  <DesktopSortFacets />
                  <ViewToggle onViewChange={handleViewChange} />
                </div>
              </div>
            </>
          )}
        </div>

        <div
          className={`items-start gap-24 px-24 pt-24 md:flex md:flex-row md:px-28 lg:px-48 xl:px-60 2xl:gap-48 2xl:px-80 ${
            !hasResults ? 'pb-0' : 'pb-48 xl:pb-0 2xl:pb-48'
          }`}
        >
          {!isCollection && (
            <div className={`${!hasResults ? '' : 'lg:block'} hidden ${bg} lg:min-w-[15rem] 2xl:min-w-[20rem]`}>
              <DesktopFacets facetsConfiguration={facetsConfiguration} />
            </div>
          )}

          <div ref={gridRef} className={`w-full ${bg} ${isCollection ? 'flex-1 lg:mt-40' : ''}`}>
            {!isCollection && (
              <div className={`${!hasResults && 'hidden'} flex items-center justify-between gap-2 lg:block`}>
                <div className="block lg:hidden">
                  <div className="cursor-pointer bg-transparent py-4 text-[14px]">
                    <ViewToggle onViewChange={handleViewChange} />
                  </div>
                </div>
                <MobileFacets facetsConfiguration={facetsConfiguration} />
              </div>
            )}

            {!hasResults ? (
              <p className="results-zero no-result-container">No products found</p>
            ) : isCollection ? (
              <div className="grid gap-16 pt-24 md:grid-cols-3 lg:grid-cols-4 lg:pt-0">
                <Tiles />
              </div>
            ) : currentView === 'list' ? (
              <div className="overflow-x-auto">
                <div className="hidden text-[15px] font-semibold text-gray-800 lg:flex">
                  <div className="w-[60%] px-6 py-4 text-left">Product</div>
                  <div className="w-[20%] px-6 py-4 text-left">Code</div>
                  <div className="w-[20%] px-6 py-4 text-left">RRP</div>
                </div>
                <div className="flex flex-col pt-32 lg:pt-0">
                  <Tiles />
                </div>
              </div>
            ) : (
              <div
                className={
                  isTabletSize
                    ? 'grid grid-cols-1 gap-16 pt-32 [&>div]:aspect-[3/4] md:grid-cols-4 lg:grid-cols-3 lg:pt-0 xl:grid-cols-4'
                    : 'grid gap-16 pt-32 md:grid-cols-2 lg:pt-0 xl:grid-cols-3'
                }
              >
                <Tiles />
              </div>
            )}

            {/* `listingTotal` sizes these to the whole listing from first paint; `disabled` keeps them
                inert for the moment before the remaining hits land. Infinite scroll needs no disabled
                flag — withholding `hasMore` already stops the sentinel from being observed. */}
            {hasResults && loadingStyle === 'pagination' && (
              <SsrPagination page={currentPage} totalPages={totalPages} onChange={goToPage} disabled={!isComplete} />
            )}
            {hasResults && loadingStyle === 'loadMore' && (
              <SsrLoadMore
                shown={visibleCount}
                total={listingTotal}
                onLoadMore={revealMore}
                disabled={!isComplete}
              />
            )}
            {hasResults && (loadingStyle === 'infinite' || !loadingStyle) && (
              <SsrInfiniteScroll onLoadMore={revealMore} hasMore={isComplete && visibleCount < hits.length} />
            )}
          </div>
        </div>
      </div>
      <ScrollToTopButton />
    </>
  );
};

export default ProductListSSR;
